import React, { useState, useEffect, useCallback, useRef } from "react"
import { OrderEntity } from "../../Objects/Order/OrderEntity"
import { EOrderStatusType } from "../../Enum/EOrderStatus"
import { ORDER_LIST_PAGE_SIZE, type OrderPaginationState, useGetOrder } from "../../hooks/Order/useGetOrder"
import { companyMemory, courierMemory, orderMemory } from "../../Objects/Memory/Memory"
import MenuLogged from "../../components/MenuLogged/MenuLogged"
import { useSaveOrder } from "../../hooks/Order/useSaveOrder"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { CourierEntity } from "../../Objects/Courier/CourierEntity"
import { useSaveDelivery } from "../../hooks/Delivery/useSaveDelivery"
import { useGetCourier } from "../../hooks/Courier/useGetCourier"
import { ECourierStatusType } from "../../Enum/ECourierStatusType"
import { AssignCourierPopup } from "./AssignCourierPopup"
import { useError } from "../../context/useErrorContext"
import { useTranslation } from "react-i18next"
import { EShippingCalculationMethodType } from "../../Enum/EShippingCalculationMethodType"
import { KitchenOrderCard } from "./Components/KitchenOrderCard"
import { useRestaurantDeliveryHub } from "../../hooks/Delivery/useRestaurantDeliveryHub"
import { getRestaurantNow } from "../../utils/timeZone"
import { EKitchenBarMode } from "../../Enum/EKitchenBarMode"
import { EOrderCurrentStatus } from "../../Enum/EOrderCurrentStatus"
import { getKitchenBarStationItems } from "./kitchenBarOrderCounters"
import { useSearchParams } from "react-router-dom"
import InputField from "../../components/InputField/InputField"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleDown, faAngleUp } from "@fortawesome/free-solid-svg-icons"

const notificationSound = new Audio("/sounds/newOrder.wav")

notificationSound.volume = 1

const getDefaultDateRange = () => {
  const dateEnd = getRestaurantNow(companyMemory.timeZoneId)
  const dateStart = getRestaurantNow(companyMemory.timeZoneId)

  dateStart.setDate(dateStart.getDate() - 1)

  return {
    dateStart: dateStart.toInputDate(),
    dateEnd: dateEnd.toInputDate(),
  }
}

interface KitchenPageProps {
  station?: "KITCHEN" | "BAR"
}

type KitchenDateRange = ReturnType<typeof getDefaultDateRange>

export const KitchenPage: React.FC<KitchenPageProps> = ({ station = "KITCHEN" }) => {
  const { t } = useTranslation(["frontend", "general"])

  const [searchParams, setSearchParams] = useSearchParams()

  const isDelayedView = searchParams.get("showDelayed") === "true"

  const normalizeDateRange = useCallback((dateStart: string, dateEnd: string) => {
    const fallbackRange = getDefaultDateRange()

    const normalizedStart = dateStart.length > 0 ? dateStart : fallbackRange.dateStart

    const normalizedEnd = dateEnd.length > 0 ? dateEnd : fallbackRange.dateEnd

    if (normalizedEnd < normalizedStart) {
      return {
        dateStart: normalizedEnd,
        dateEnd: normalizedStart,
      }
    }

    return {
      dateStart: normalizedStart,
      dateEnd: normalizedEnd,
    }
  }, [])

  const getDateRangeFromSearchParams = useCallback(
    (params: URLSearchParams = searchParams) => {
      return normalizeDateRange(params.get("dateStart")?.trim() ?? "", params.get("dateEnd")?.trim() ?? "")
    },
    [normalizeDateRange, searchParams],
  )

  const [orders, setOrders] = useState<OrderEntity[]>([])

  const [appliedDateRange, setAppliedDateRange] = useState(getDateRangeFromSearchParams)

  const [draftDateRange, setDraftDateRange] = useState(getDateRangeFromSearchParams)

  const [isFilterPanelExpanded, setIsFilterPanelExpanded] = useState(false)

  const [paginationState, setPaginationState] = useState<OrderPaginationState>({
    pageNumber: 1,
    pageSize: ORDER_LIST_PAGE_SIZE,
    totalCount: 0,
    hasNextPage: false,
  })

  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const pendingCountRef = useRef(0)

  const [hasNewPending, setHasNewPending] = useState(false)

  const [filter, setFilter] = useState<EOrderStatusType | "ALL">(EOrderStatusType.Pending)

  const [isAssignOpen, setIsAssignOpen] = useState(false)

  const [assignOrder, setAssignOrder] = useState<OrderEntity | null>(null)

  const [couriers, setCouriers] = useState<CourierEntity[]>([])

  const [selectedCourierId, setSelectedCourierId] = useState<string>("")

  const { setError } = useError()

  const { handleGetOrder } = useGetOrder()

  const handleGetOrderRef = useRef(handleGetOrder)

  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  const { handleSaveOrderStatus } = useSaveOrder()

  const { handleAssignDelivery, handleCancelSearchDelivery } = useSaveDelivery()

  const { handleGetCourier } = useGetCourier()

  const handleUpdateStatus = (id: number, orderStatusType: EOrderStatusType) => {
    handleSaveOrderStatus(id, orderStatusType, () => fetchOrders())
  }

  const handleCancelSearch = (deliveryId: number) => {
    handleCancelSearchDelivery(deliveryId, fetchOrders)
  }

  const handleOpenAssignCourier = (order: OrderEntity) => {
    setAssignOrder(order)

    setSelectedCourierId("")

    setIsAssignOpen(true)
  }

  const handleCloseAssignCourier = () => {
    setIsAssignOpen(false)

    setAssignOrder(null)
  }

  const handleLoadCouriers = () => {
    handleGetCourier(() => {
      setCouriers(courierMemory.listData.map((e) => new CourierEntity(e)))
    })
  }

  const isFreeShippingAssignOrder =
    assignOrder?.orderPaymentEntity?.shippingCalculationMethod === EShippingCalculationMethodType.Free

  const handleConfirmAssign = () => {
    if (!assignOrder || !assignOrder.addressEntity) {
      setError(t("general:label_title_erro"), t("kitchen.address_not_found"))

      return
    }

    if (selectedCourierId) {
      const selectedCourier = couriers.find((courier) => courier.id === selectedCourierId)

      if (!selectedCourier || selectedCourier.status !== ECourierStatusType.Online) {
        return
      }
    }

    handleAssignDelivery(
      {
        orderId: assignOrder.id,
        courierEntityId: selectedCourierId || undefined,
        hasAcceptedFinancialResponsibility: true,
      },
      () => {
        fetchOrders()

        handleCloseAssignCourier()
      },
    )
  }

  useEffect(() => {
    if (isAssignOpen) {
      handleLoadCouriers()
    }
  }, [isAssignOpen])

  const isSeparatedMode = companyMemory.kitchenBarMode === EKitchenBarMode.Separated

  const getStationItems = useCallback(
    (order: OrderEntity) => {
      return getKitchenBarStationItems(order, station, companyMemory.kitchenBarMode)
    },
    [station],
  )

  const activeOrdersCount = orders.filter((e) => e.eOrderStatusType !== EOrderStatusType.Delivered).length

  const filteredOrders = orders.filter((e) => {
    if (filter === "ALL") {
      return e.eOrderStatusType !== EOrderStatusType.Delivered
    }

    if (filter === EOrderStatusType.InDelivery) {
      return e.eOrderStatusType === EOrderStatusType.InDelivery
    }

    if (filter === EOrderStatusType.Delivered) {
      return e.eOrderStatusType === EOrderStatusType.Delivered
    }

    return e.getAllGenericFoods().some((e) => e.currentStatus === filter)
  })

  const hasVisibleItemsForCurrentFilter = useCallback(
    (order: OrderEntity) => {
      const stationItems = getStationItems(order)

      if (!stationItems || stationItems.length === 0) {
        return false
      }

      if (filter === "ALL") {
        return stationItems.some((item) => item.currentStatus !== EOrderCurrentStatus.Closed)
      }

      if (filter === EOrderStatusType.InDelivery || filter === EOrderStatusType.Delivered) {
        return true
      }

      return stationItems.some(
        (item) => item.currentStatus === filter || item.currentStatus === EOrderCurrentStatus.Closed,
      )
    },
    [filter, getStationItems],
  )

  const stationOrders = filteredOrders.filter(hasVisibleItemsForCurrentFilter)

  useEffect(() => {
    handleGetOrderRef.current = handleGetOrder
  }, [handleGetOrder])

  const getTabCounter = useCallback(
    (targetStatus: EOrderStatusType) => {
      return orders.filter((order) => {
        const stationItems = getStationItems(order)

        if (!stationItems || stationItems.length === 0) {
          return false
        }

        if (targetStatus === EOrderStatusType.InDelivery) {
          return order.eOrderStatusType === EOrderStatusType.InDelivery
        }

        return stationItems.some((item) => item.currentStatus === targetStatus)
      }).length
    },
    [orders, getStationItems],
  )

  const checkNewPendingOrders = useCallback(
    (newOrders: OrderEntity[]) => {
      const newPending = newOrders.filter((order) => {
        const stationItems = getStationItems(order)

        if (!stationItems || stationItems.length === 0) {
          return false
        }

        return stationItems.some((item) => item.currentStatus === EOrderStatusType.Pending)
      }).length

      if (newPending > pendingCountRef.current) {
        setHasNewPending(true)
        notificationSound.play().catch(() => {})
      }

      pendingCountRef.current = newPending
    },
    [getStationItems],
  )

  const fetchOrdersPage = useCallback(
    (pageNumber: number, append: boolean, dateRange?: KitchenDateRange | null) => {
      setHasNewPending(false)

      const safeDateRange = dateRange ?? appliedDateRange

      const normalizedRange = normalizeDateRange(safeDateRange.dateStart, safeDateRange.dateEnd)

      void handleGetOrderRef
        .current(
          () => {
            const data = orderMemory.listData

            setOrders(data)

            checkNewPendingOrders(data)
          },
          normalizedRange.dateStart,
          normalizedRange.dateEnd,
          undefined,
          undefined,
          false,
          isDelayedView,
          {
            pageNumber,
            pageSize: ORDER_LIST_PAGE_SIZE,
            append,
          },
        )
        .then((nextPaginationState) => {
          if (nextPaginationState) {
            setPaginationState(nextPaginationState)
          }
        })
    },
    [appliedDateRange, checkNewPendingOrders, isDelayedView, normalizeDateRange],
  )

  const fetchOrders = useCallback(
    (dateRange?: KitchenDateRange | null) => {
      fetchOrdersPage(1, false, dateRange)
    },
    [fetchOrdersPage],
  )

  const handleLoadMoreOrders = useCallback(() => {
    if (isLoadingMore || !paginationState.hasNextPage) {
      return
    }

    setIsLoadingMore(true)

    const safeDateRange = appliedDateRange

    const normalizedRange = normalizeDateRange(safeDateRange.dateStart, safeDateRange.dateEnd)

    handleGetOrderRef
      .current(
        () => {
          const data = orderMemory.listData

          setOrders(data)

          checkNewPendingOrders(data)
        },
        normalizedRange.dateStart,
        normalizedRange.dateEnd,
        undefined,
        undefined,
        false,
        isDelayedView,
        {
          pageNumber: paginationState.pageNumber + 1,
          pageSize: ORDER_LIST_PAGE_SIZE,
          append: true,
        },
      )
      .then((nextPaginationState) => {
        if (nextPaginationState) {
          setPaginationState(nextPaginationState)
        }
      })
      .finally(() => {
        setIsLoadingMore(false)
      })
  }, [
    appliedDateRange,
    checkNewPendingOrders,
    isDelayedView,
    isLoadingMore,
    normalizeDateRange,
    paginationState.hasNextPage,
    paginationState.pageNumber,
  ])

  useEffect(() => {
    const loadMoreElement = loadMoreRef.current

    if (!loadMoreElement || !paginationState.hasNextPage || isLoadingMore) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          handleLoadMoreOrders()
        }
      },
      { rootMargin: "600px 0px" },
    )

    observer.observe(loadMoreElement)

    return () => observer.disconnect()
  }, [handleLoadMoreOrders, isLoadingMore, paginationState.hasNextPage])

  const handleApplyDateFilter = () => {
    const normalizedRange = normalizeDateRange(draftDateRange.dateStart, draftDateRange.dateEnd)
    const nextSearchParams = new URLSearchParams(searchParams)

    setDraftDateRange(normalizedRange)

    setAppliedDateRange(normalizedRange)

    nextSearchParams.set("dateStart", normalizedRange.dateStart)

    nextSearchParams.set("dateEnd", normalizedRange.dateEnd)

    if (isDelayedView) {
      nextSearchParams.delete("showDelayed")
    }

    setSearchParams(nextSearchParams, { replace: true })
  }

  useRestaurantDeliveryHub(fetchOrders)

  useEffect(() => {
    if (isDelayedView) {
      setFilter("ALL")
    }
  }, [isDelayedView])

  useEffect(() => {
    const urlDateRange = getDateRangeFromSearchParams()

    setAppliedDateRange(urlDateRange)
    setDraftDateRange(urlDateRange)
  }, [getDateRangeFromSearchParams])

  useEffect(() => {
    fetchOrders()

    const interval = setInterval(fetchOrders, 30000)

    return () => clearInterval(interval)
  }, [fetchOrders])

  const tabs = [
    { label: t("kitchen.pending"), val: EOrderStatusType.Pending },
    { label: t("kitchen.preparing"), val: EOrderStatusType.Preparing },
    { label: t("kitchen.ready_for_delivery"), val: EOrderStatusType.PreparingFinished },
    { label: t("kitchen.out_for_delivery"), val: EOrderStatusType.InDelivery },
    { label: t("kitchen.finished"), val: EOrderStatusType.Delivered },
    { label: t("kitchen.all_not_finished"), val: "ALL" },
  ]

  return (
    <Animate type={AnimationTypes.fadeIn}>
      <div className="h-dvh flex flex-col bg-white font-inter">
        <MenuLogged enableButtons={false} />

        <main className="flex-1 overflow-y-auto p-2 md:p-6">
          <div className="mb-4 space-y-2">
            <div className="flex flex-wrap gap-2 p-2 rounded-[0.5rem] ring-2 ring-slate-300 bg-slate-100">
              {tabs.map((tab) => {
                const isPendingTab = tab.val === EOrderStatusType.Pending
                const showAlert = isPendingTab && hasNewPending
                const showCounter =
                  tab.val === EOrderStatusType.Pending ||
                  tab.val === EOrderStatusType.Preparing ||
                  tab.val === EOrderStatusType.PreparingFinished ||
                  tab.val === EOrderStatusType.InDelivery
                const tabCount = typeof tab.val === "number" ? getTabCounter(tab.val) : 0

                return (
                  <button
                    key={tab.label}
                    onClick={() => {
                      setHasNewPending(false)

                      setFilter(tab.val as any)
                    }}
                    className={`
                      relative px-4 py-2 rounded-lg text-sm text-slate-900 font-bold transition-all
                      ${filter === tab.val ? "bg-white ring-2 ring-slate-400 shadow-sm" : "hover:bg-white"}
                      ${showAlert ? "ring-2 bg-orange-200 ring-orange-700 animate-[pulse_1s_ease-in-out_infinite]" : ""}
                    `}
                    data-interaction-description-key="interactionDescriptions:pages.kitchenPage.kitchenPage.button.button"
                  >
                    {tab.label}

                    {showCounter && tabCount > 0 && (
                      <span
                        className={`
                          absolute -top-2 -right-2 min-w-[20px] h-[20px]
                          px-1.5 flex items-center justify-center
                          text-[11px] ring-1 ring-red-800 font-bold text-white
                          bg-red-500 rounded-full shadow-md
                          ${showAlert ? "animate-bounce" : ""}
                        `}
                      >
                        {tabCount > 99 ? "99+" : tabCount}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>

            <div className="flex flex-wrap items-center gap-3 rounded-[0.5rem] bg-slate-100 p-3 ring-2 ring-slate-300">
              <div className="flex min-w-0 flex-1 flex-wrap gap-3">
                {isDelayedView && (
                  <div className="bg-red-50 px-4 py-2 rounded-[0.5rem] ring-2 ring-red-200 text-sm font-bold text-red-800">
                    {t("kitchen.delayed_filter_active")}
                  </div>
                )}

                <div className="bg-white px-4 py-2 rounded-[0.5rem] ring-2 ring-slate-200 text-sm font-medium text-slate-700">
                  {t("kitchen.active")}: <span className="font-bold text-slate-900">{activeOrdersCount}</span>
                </div>

                <div className="bg-white px-4 py-2 rounded-[0.5rem] ring-2 ring-slate-200 text-sm font-medium text-slate-700">
                  {t("kitchen.showing")}: <span className="font-bold text-slate-900">{stationOrders.length}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsFilterPanelExpanded((current) => !current)}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white text-slate-800 ring-2 ring-slate-200 transition-colors hover:bg-slate-50 lg:hidden"
                aria-label={isFilterPanelExpanded ? t("kitchen.collapse_filters") : t("kitchen.expand_filters")}
                title={isFilterPanelExpanded ? t("kitchen.collapse_filters") : t("kitchen.expand_filters")}
                data-interaction-description-key="interactionDescriptions:pages.kitchenPage.kitchenPage.button.collapseFilters"
              >
                <FontAwesomeIcon icon={isFilterPanelExpanded ? faAngleUp : faAngleDown} />
              </button>

              <div
                className={`${isFilterPanelExpanded ? "grid" : "hidden"} w-full gap-3 sm:grid-cols-[minmax(150px,220px)_minmax(150px,220px)_auto] sm:items-center lg:ml-auto lg:grid lg:w-auto`}
              >
                <InputField
                  type="date"
                  value={draftDateRange.dateStart}
                  onChange={(event) => setDraftDateRange((current) => ({ ...current, dateStart: event.target.value }))}
                  placeholder={t("kitchen.date_start")}
                  animationTypes={AnimationTypes.fadeIn}
                  descriptionKey="interactionDescriptions:pages.kitchenPage.kitchenPage.input.dateStart"
                />

                <InputField
                  type="date"
                  value={draftDateRange.dateEnd}
                  onChange={(event) => setDraftDateRange((current) => ({ ...current, dateEnd: event.target.value }))}
                  placeholder={t("kitchen.date_end")}
                  animationTypes={AnimationTypes.fadeIn}
                  descriptionKey="interactionDescriptions:pages.kitchenPage.kitchenPage.input.dateEnd"
                />

                <button
                  type="button"
                  onClick={handleApplyDateFilter}
                  className="h-11 rounded-md bg-slate-900 px-4 text-sm font-bold text-white transition-colors hover:bg-slate-700"
                  data-interaction-description-key="interactionDescriptions:pages.kitchenPage.kitchenPage.button.handleApplyDateFilter"
                >
                  {t("kitchen.apply_filter")}
                </button>
              </div>
            </div>
          </div>

          {stationOrders.length > 0 ? (
            <>
              <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                {stationOrders.map((order) => (
                  <KitchenOrderCard
                    key={order.id}
                    type={filter}
                    order={order}
                    station={station}
                    isSeparatedMode={isSeparatedMode}
                    onUpdate={fetchOrders}
                    onUpdateStatus={handleUpdateStatus}
                    onSelectCourier={handleOpenAssignCourier}
                    onCancelSearch={handleCancelSearch}
                  />
                ))}
              </div>

              <div ref={loadMoreRef} className="flex min-h-12 items-center justify-center py-4">
                {isLoadingMore && (
                  <span className="h-6 w-6 animate-spin rounded-full border-4 border-orange-200 border-t-orange-500" />
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-slate-300">
              <svg className="w-20 h-20 opacity-20 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"
                />
              </svg>

              <p className="uppercase tracking-widest text-lg font-semibold">{t("kitchen.clean_station")}</p>
            </div>
          )}
        </main>

        <AssignCourierPopup
          isOpen={isAssignOpen}
          couriers={couriers}
          selectedCourierId={selectedCourierId}
          allowRandomCourier={!isFreeShippingAssignOrder}
          onClose={handleCloseAssignCourier}
          onSelectCourier={setSelectedCourierId}
          onConfirmSelected={handleConfirmAssign}
          onConfirmRandom={handleConfirmAssign}
        />

        <footer className="border-t bg-white px-6 py-4 text-[12px] text-slate-500 flex flex-col md:flex-row md:justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></div>

            <span className="font-bold text-green-600">{t("kitchen.connected")}</span>
          </div>

          <span className="font-bold text-gray-600">
            {t("kitchen.last_update")} {getRestaurantNow(companyMemory.timeZoneId).toLocaleTimeString()}
          </span>
        </footer>
      </div>
    </Animate>
  )
}
